import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Alert, Empty} from "antd";
import moment from "moment";

import HeaderSection from "./components/HeaderSection";
import SensorsGroups from "./components/SensorsGroups";
import DashboardAlert from "../../components/Alerts/dachboard";
import Versions from "./components/Versions";
import SystemTable from "./components/SystemState";

import {useCurrentSelection} from "../../hooks/useCurrentSelection";
import {sensorsAC} from "../../store/branches/sensors/actionCreators";

import {ReactComponent as Warning} from "../../assets/icons/maintanence_warning.svg";
import {ConfirmStatus, Maintenance} from "../../store/branches/sensors/stateTypes";
import {LoadingStatus} from "../../store/status";
import {selectDevicesState, selectSensorsState, selectSiloGroupsState} from "../../store/selectors";
import Preloader from "../../components/Preloader";
import Spinner from "../../components/Spinner";
import MaintenanceModal from "../../components/MaintenanceModal";
import FailedMaintenanceModal from "../../components/MaintenanceModal/failedMaintenanceModal";

import classes from "./SensorDashboard.module.scss";
import {useSocketSensors} from "../../hooks/useSocket";
import GrainSiloDeviceView from "./components/GrainSiloDeviceView";
import RollStandDeviceView from "./components/RollStandDeviceView";
import {devicesAC} from "../../store/branches/devices/actionCreators";
import {CLI_GROUP_ID, deriveInput910Groups} from "./utils/input910";

const maintenanceTime = process.env.REACT_APP_MAINTENANCE_TIME;

const SensorDashboard: React.FC = () => {
    const dispatch = useDispatch();

    const {
        ws_data, filter_status, status, maintenance_status_operation,
        status_operation,
        maintenanceSensorsArray, isMaintenance, confirmMaintenance,
        isSnapshot, sensorSettings,
    } = useSelector(selectSensorsState);
    const {maintenanceInfo, last_event} = useSelector(selectDevicesState);
    const {device} = useCurrentSelection();

    const [isLoading, setLoading] = useState<boolean>(true);
    const failed = useRef<any>();
    const isCachedData = !ws_data && !!last_event;


    const data = ws_data || last_event;
    const cachedEventDate = last_event?.timestamp?.$date
        ? moment(last_event.timestamp.$date).format("Do MMMM YYYY, HH:mm")
        : null;

    const isDoneMaintenanceAfterReload = !!maintenanceInfo && (!!Object.values(maintenanceInfo).filter(item => item !== null).length);

    const onSubmitComment = (message: string) => {
        dispatch(sensorsAC.showConfirmModal({isShow: false}));
        dispatch(sensorsAC.stopSensorMaintenance({
            device_id: confirmMaintenance.device_id,
            event_type: confirmMaintenance.event_type,
            sensor_id: confirmMaintenance.sensor_id,
            sensor_name: confirmMaintenance.sensor_name,
            comment: message,
            maintenance_time: new Date().getTime(),
        }));
        dispatch(sensorsAC.clearMaintenanceState());
    };

    const failedMaintenance = (item: Maintenance) => {
        failed.current = {
            isShow: true,
            device_id: item.device_id,
            event_type: "maintenance_failed",
            current_sensor_id: item.current_sensor_id,
            sensor_id: item.sensor_id,
            sensor_name: item.sensor_name,
            groupName: item.groupName
        };
    };

    const failedMaintenanceClientSide = useCallback((item: Maintenance) => {
        dispatch(sensorsAC.showConfirmModal({
            isShow: true,
            status: ConfirmStatus.failed,
            device_id: item.device_id,
            event_type: "maintenance_failed",
            sensor_id: item.sensor_id,
            sensor_name: item.sensor_name,
            groupName: item.groupName
        }));
    }, [dispatch]);

    const checkTime = (sensorsArray: Maintenance[]) => {
        if (sensorsArray.length > 0) {
            sensorsArray.map((item: Maintenance, index: number) => {
                const now = new Date().getTime(); // now moment in seconds
                const differenceTime = (now - item.maintenance_time) / 1000; //find out the difference between start and now
                if (Number(differenceTime.toFixed()) > Number(maintenanceTime)) {
                    if (!isDoneMaintenanceAfterReload) {
                        failedMaintenanceClientSide(item);
                    } else {
                        failedMaintenance(item);
                    }
                    dispatch(sensorsAC.clearMaintenanceArray(item.sensor_id));
                }
                return null;
            });
        }
        return null;
    };

    useEffect(() => {
        if (isMaintenance && maintenanceSensorsArray?.length > 0) {
            checkTime(maintenanceSensorsArray);
        }
        if (isMaintenance && maintenanceInfo && !isDoneMaintenanceAfterReload) {
            failed.current = {isShow: false};
        }
    });

    useEffect(() => {
        if (device?.id) {
            dispatch(sensorsAC.fetchSensorImages(device?.id.toString()));
        }
    }, [dispatch, device?.id]);

    // Input 9/10 (IE-Node-1 only): fetch the admin's declared CLI/Pulse/Contact type
    // per input, so the live dashboard can pick the matching sensor to display (see
    // utils/input910.ts). Reuses the same settings endpoint EditSensorSettings does.
    useEffect(() => {
        if (device?.id && device?.device_type === "0005") {
            dispatch(sensorsAC.fetchSensorSettings(device.id));
        }
    }, [dispatch, device?.id, device?.device_type]);

    const inputTypeBySensorId = useMemo(() => {
        const cliGroup = sensorSettings?.find((group) => group.id === CLI_GROUP_ID);
        const map: Record<string, string> = {};
        cliGroup?.sensors?.forEach((sensor) => {
            map[sensor.id] = sensor.type;
        });
        return map;
    }, [sensorSettings]);

    // Splits the raw "Pulse"/"CLI" groups into separate Pulse/Contact/CLI sections
    // with the correct heading, per input — see utils/input910.ts. No-op for any
    // device without both a Pulse and a CLI group (i.e. anything but IE-Node-1).
    const displayedGroups = useMemo(
        () => deriveInput910Groups(data?.groups, inputTypeBySensorId),
        [data?.groups, inputTypeBySensorId]
    );

    useEffect(() => {
        if (ws_data || !device?.id) {
            return;
        }
        dispatch(devicesAC.fetchLastEvent(device.id));
    }, [dispatch, ws_data, device?.id]);

    useEffect(() => {
        setTimeout(() => {
            setLoading(false);
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            dispatch(sensorsAC.clearWsSensorsData());
            dispatch(sensorsAC.getOutSnapshotSensors());
        };
    }, [dispatch, device]);

    // fix this after backand will change
    useSocketSensors((device && !isSnapshot) ? `ws/device-data/${device?.udf_id}/` : "");

    useEffect(() => {
        // Validate maintenance state against actual sensor status
        if (isMaintenance && maintenanceSensorsArray.length === 0 && !maintenanceInfo) {
            dispatch(sensorsAC.clearMaintenanceState());
        }
    }, [isMaintenance, maintenanceSensorsArray, maintenanceInfo, dispatch]);

    if ((maintenance_status_operation === LoadingStatus.LOADING) || isLoading || status_operation === LoadingStatus.FIX_NOTIFICATION_LOADING) {
        return <Spinner/>;
    }

    return (
        <Preloader isLoaded={status === LoadingStatus.LOADING && !ws_data}>
            <div className={classes.SensorDashboardWrap}>
                <DashboardAlert/>
                <HeaderSection isSnapshot={isSnapshot}/>

                <MaintenanceModal isModal={confirmMaintenance.isShow}
                                  status={confirmMaintenance.status}
                                  onSubmit={onSubmitComment}
                                  sensor_name={confirmMaintenance.sensor_name}
                                  groupName={confirmMaintenance.groupName}
                                  />

                {
                    failed.current?.isShow
                    &&
                    <FailedMaintenanceModal isShow={failed.current.isShow}
                                            device_id={failed.current.device_id}
                                            event_type={failed.current.event_type}
                                            sensor_id={failed.current.sensor_id}
                                            current_sensor_id={failed.current.current_sensor_id}
                                            sensor_name={failed.current.sensor_name}
                                            groupName={failed.current.groupName}
                    />
                }
                {
                    !ws_data && !last_event
                    &&
                    <Empty
                        description="Device is Offline! No notifications can occur until the device is back online! Please check the device's internet connection"/>
                }
                {
                    isCachedData && !device.is_online
                    &&
                    <Alert
                        type="warning"
                        showIcon
                        message={`Device is offline — showing cached data${cachedEventDate ? ` from ${cachedEventDate}` : ""}`}
                        style={{margin: "12px 0"}}
                    />
                }
                  {
                    isCachedData && device.is_online
                    &&
                    <Alert
                        type="warning"
                        showIcon
                        message={`Waiting for next live event — showing cached data${cachedEventDate ? ` from ${cachedEventDate}` : ""}`}
                        style={{margin: "12px 0"}}
                    />
                }
                {
                    isMaintenance && maintenanceSensorsArray?.length === 0
                    &&
                    <div className={classes.warningMaintenance}>
                        <div className={classes.icon}>
                            <Warning/>
                        </div>
                        <div className={classes.description}>
                            <span>Please click on the sensor to start the maintenance test.</span>
                        </div>
                    </div>
                }
    {
                    isMaintenance && maintenanceSensorsArray?.length === 1
                    &&
                    <div className={classes.warningMaintenance}>
                        <div className={classes.icon}>
                            <Warning/>
                        </div>
                        <div className={classes.description}>
                            <span>Testing in progress.</span>
                        </div>
                    </div>
                }
                {
                    device?.device_type === "F500-UDF"
                    &&
                    <SystemTable data={data}/>
                }

             {

                 device?.device_sub_type?.name ==="Roll Stand" ?
                        <RollStandDeviceView  key={device?.udf_id}  ws_data={data} />
                     :

                    data?.device?.deviceType === "0007" || data?.device?.deviceType === "001W" ?
                        <GrainSiloDeviceView  key={device?.udf_id}  ws_data={data} />

           :
                    data && displayedGroups?.map((item, index) => (
                        <SensorsGroups group={item}
                                       wsDataId={data.Id}
                                       groupId={item.Id}
                                       key={`${item?.Id}sensor_group${index}`}
                                       sensorsGroupsName={data.Name}
                                       groupNumber={index + 1}
                                       deviceType={data?.device?.deviceType}
                                       groupName={item.Name}
                                       filter_status={filter_status}
                        />
                    ))
                }

                {
                    data?.groups && data?.device?.versionInfo
                        ?
                        <Versions versions={data?.device?.versionInfo}/>
                        :
                        <div style={{margin: "12px"}}>
                            <p>Version information not available</p>
                        </div>
                }
            </div>
        </Preloader>
    );
};


export default SensorDashboard;
